import React, { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { AlertTriangle, RefreshCw, CheckCircle2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { tr, currentLang } from "./i18n";

const intlLocale = () => (currentLang() === "en" ? "en-GB" : "uk-UA");

export const CHANGE_TYPE_UK = {
  budget_change: "Зміна бюджету",
  deadline_change: "Зміна дедлайну",
  scope_change: "Зміна обсягу робіт",
};
const getChangeTypeLabel = (kind) => tr(CHANGE_TYPE_UK[kind] || kind);

/** Approve / reject a budget-deadline-scope change on a task. */
const ChangeRequestDialog = ({ req, onClose, onDone, readOnly }) => {
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const respond = async (action) => {
    if (readOnly) return toast.info(tr("Режим перегляду — дія недоступна"));
    setSubmitting(true);
    try {
      await api.put(`/client/task-change-requests/${req.id}`, { action, comment });
      toast.success(action === "approve" ? tr("Зміни схвалено") : tr("Зміни відхилено"));
      onClose();
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? tr("Режим перегляду — дія недоступна") : tr("Не вдалося обробити запит"));
    } finally {
      setSubmitting(false);
    }
  };
  return (
  <Dialog 
    open={!!req} 
    onOpenChange={() => { onClose(); }}
  >
    <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
      <DialogHeader>
        <DialogTitle className="text-zinc-50 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-400" />
          {tr("Запит на зміни")}
        </DialogTitle>
      </DialogHeader>
      
      {req && (
        <div className="space-y-4 py-2">
          {/* Task & Project info */}
          <div className="p-3 bg-zinc-800/50 rounded-lg">
            <p className="text-sm font-medium text-zinc-50">{req.task_name}</p>
            <p className="text-xs text-zinc-500">{req.project_name}</p>
          </div>
          
          {/* Change type badge */}
          <Badge variant="outline" className="text-sm bg-amber-500/20 text-amber-400 border-amber-500/30">
            {getChangeTypeLabel(req.change_type)}
          </Badge>
          
          {/* Change details */}
          <div className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700/50 space-y-3">
            {req.change_type === "budget_change" && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-zinc-400">{tr("Поточний бюджет:")}</span>
                  <span className="text-sm text-red-400 line-through">
                    {req.old_client_price?.toLocaleString()} {req.currency || "$"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-zinc-400">{tr("Новий бюджет:")}</span>
                  <span className="text-sm text-green-400 font-semibold">
                    {req.new_client_price?.toLocaleString()} {req.currency || "$"}
                  </span>
                </div>
                {req.old_client_price && req.new_client_price && (
                  <div className="flex justify-between items-center pt-2 border-t border-zinc-700">
                    <span className="text-sm text-zinc-400">{tr("Різниця:")}</span>
                    <span className={cn(
                      "text-sm font-semibold",
                      req.new_client_price > req.old_client_price ? "text-red-400" : "text-green-400"
                    )}>
                      {req.new_client_price > req.old_client_price ? "+" : ""}
                      {(req.new_client_price - req.old_client_price).toLocaleString()} {req.currency || "$"}
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {req.change_type === "deadline_change" && (
              <div className="space-y-2">
                {req.old_deadline && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-400">{tr("Поточний дедлайн:")}</span>
                    <span className="text-sm text-red-400 line-through">
                      {new Date(req.old_deadline).toLocaleDateString(intlLocale())}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-zinc-400">{tr("Новий дедлайн:")}</span>
                  <span className="text-sm text-green-400 font-semibold">
                    {req.new_deadline 
                      ? new Date(req.new_deadline).toLocaleDateString(intlLocale())
                      : tr("Не вказано")}
                  </span>
                </div>
              </div>
            )}
            
            {/* Scope change includes both budget and deadline */}
            {req.change_type === "scope_change" && (
              <div className="space-y-3">
                {/* Budget changes if present */}
                {(req.old_client_price !== undefined || req.new_client_price !== undefined) && (
                  <div className="space-y-2">
                    <p className="text-xs text-zinc-500 font-medium">{tr("Бюджет:")}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-400">{tr("Поточний:")}</span>
                      <span className="text-sm text-red-400 line-through">
                        {req.old_client_price?.toLocaleString() || req.original_client_price?.toLocaleString()} {req.currency || "$"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-400">{tr("Новий:")}</span>
                      <span className="text-sm text-green-400 font-semibold">
                        {req.new_client_price?.toLocaleString()} {req.currency || "$"}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Deadline changes if present */}
                {req.new_deadline && (
                  <div className="space-y-2 pt-2 border-t border-zinc-700">
                    <p className="text-xs text-zinc-500 font-medium">{tr("Дедлайн:")}</p>
                    {(req.old_deadline || req.original_deadline) && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-zinc-400">{tr("Поточний:")}</span>
                        <span className="text-sm text-red-400 line-through">
                          {new Date(req.old_deadline || req.original_deadline).toLocaleDateString(intlLocale())}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-400">{tr("Новий:")}</span>
                      <span className="text-sm text-green-400 font-semibold">
                        {new Date(req.new_deadline).toLocaleDateString(intlLocale())}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Reason */}
          {req.reason && (
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-xs text-zinc-500 mb-1">{tr("Причина зміни:")}</p>
              <p className="text-sm text-zinc-300">{req.reason}</p>
            </div>
          )}
          
          {/* Comment input */}
          <div>
            <Label className="text-zinc-400 mb-2 block text-sm">
              {tr("Ваш коментар (необов'язково)")}
            </Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={tr("Додайте коментар до вашого рішення...")}
              className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
            />
          </div>
          
          {/* Action buttons */}
          <div className="flex gap-2 justify-end pt-2 border-t border-zinc-800">
            <Button
              variant="outline"
              onClick={() => respond("reject")}
              disabled={submitting}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              {submitting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <AlertTriangle className="h-4 w-4 mr-2" />
              )}
              {tr("Відхилити")}
            </Button>
            <Button
              onClick={() => respond("approve")}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              {tr("Схвалити")}
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
  );
};

export default ChangeRequestDialog;
